import { getCurrentInstance } from 'vue'

declare global {
  interface Window {
    /** Flutter(iOS/Android) から uuid と FCM トークンを直接渡してくる */
    saveToken?: (uuid: string, deviceToken: string) => void
    /** Flutter(iOS) から FCM トークンだけ渡してくる */
    fromApp?: (deviceToken: string) => void
    /** Flutter(Android) から FCM トークンだけ渡してくる。iOS と同一処理 */
    fromApp4Android?: (deviceToken: string) => void
  }
}

type DeviceTokenHostVm = {
  $varuna: { endPoints: Record<string, string> }
  $globalVar: { deviceToken?: string }
  $firebase: { user: { uid: string } | null }
}

export type UseAppDeviceTokenBridgeReturn = {
  /** ログイン済みかつトークン保持時に、現在のユーザーの device_token を送る */
  syncCurrentUserDeviceToken: () => void
}

/**
 * Flutter アプリ(WebView) から呼ばれる window ブリッジ。
 * App.vue の onBeforeMount に直書きされていた saveToken / fromApp / fromApp4Android を移設した。
 * fromApp と fromApp4Android は元から同一処理だったので 1 実装に統合している。
 *
 * $varuna / $globalVar / $firebase はまだ Vue.prototype 側が正本のため、
 * 移設のための一時ブリッジとして getCurrentInstance().proxy から取得する（新規依存は増やさない）。
 */
export function useAppDeviceTokenBridge(options: {
  /** 現在の Firebase ユーザー（App.vue 側の解決ロジックをそのまま使う） */
  currentUser: () => { uid: string } | null
  /** 実際の POST。App.vue の updateDeviceToken を渡す */
  postDeviceToken: (uuid: string, deviceToken: string) => void
}): UseAppDeviceTokenBridgeReturn {
  const vm = getCurrentInstance()?.proxy as unknown as DeviceTokenHostVm | null

  const syncCurrentUserDeviceToken = () => {
    const firebaseUser = options.currentUser()
    if (!firebaseUser) return
    const deviceToken = vm?.$globalVar?.deviceToken
    if (!deviceToken || deviceToken.trim() === '') return
    options.postDeviceToken(firebaseUser.uid, deviceToken)
  }

  const receiveDeviceTokenFromApp = (deviceToken: string) => {
    if (!vm) return
    vm.$globalVar.deviceToken = deviceToken
    syncCurrentUserDeviceToken()
  }

  window.saveToken = options.postDeviceToken
  window.fromApp = receiveDeviceTokenFromApp
  window.fromApp4Android = receiveDeviceTokenFromApp

  return { syncCurrentUserDeviceToken }
}
